'use client';

import { FormattedMessage } from 'react-intl';
import { BaseStyleCardConfig, BaseStyleId, DynamicStyleCardConfig, DynamicStyleId, StaticStyleCardConfig, StaticStyleId } from '../types/Styles';

export const baseStyleCardConfigs: Record<BaseStyleId, BaseStyleCardConfig> = {
  adminBasemap: {
    displayName: 'Custom basemap',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-adminBasemap@2x.png',
    key: 'adminBasemap',
    requiresAdmin: true,
    requiresPLP: true,
    requiresAdminStyleSourceUrl: true
  },
  alltrailsOutdoorsV2: {
    displayName: 'AllTrails',
    description: <FormattedMessage defaultMessage="Extensive trail & terrain map" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsOutdoorsV2@2x.png',
    key: 'alltrailsOutdoorsV2',
    supports3d: true
  },
  alltrailsSatellite: {
    displayName: <FormattedMessage defaultMessage="Satellite" />,
    description: <FormattedMessage defaultMessage="Detailed satellite imagery" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-alltrailsSatellite@2x.png',
    key: 'alltrailsSatellite',
    supports3d: true
  },
  alltrailsSatelliteMinimal: {
    displayName: 'Satellite minimal',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-alltrailsSatellite@2x.png',
    key: 'alltrailsSatelliteMinimal',
    supports3d: true
  },
  googleSatellite: {
    displayName: 'Google satellite',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-googleSatellite@2x.png',
    key: 'googleSatellite',
    requiresAdmin: true
  },
  ignPlan: {
    displayName: <FormattedMessage defaultMessage="IGN Plan" />,
    description: <FormattedMessage defaultMessage="Trails, streets, & landmarks in France" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-ignPlan@2x.png',
    key: 'ignPlan',
    requiresAdmin: false
  },
  ignScan: {
    displayName: <FormattedMessage defaultMessage="IGN SCAN 25" />,
    description: <FormattedMessage defaultMessage="Detailed topographic map of France" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-ignScan@2x.png',
    key: 'ignScan',
    requiresAdmin: false
  },
  opencycle: {
    displayName: 'OpenCycleMap (OCM)',
    description: <FormattedMessage defaultMessage="World map with cycling routes" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-opencycle-2@2x.png',
    key: 'opencycle',
    requiresAdmin: false
  },
  openstreet: {
    displayName: 'OpenStreetMap (OSM)',
    description: <FormattedMessage defaultMessage="World map with detailed landmarks" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-openstreet-2@2x.png',
    key: 'openstreet',
    requiresAdmin: false
  },
  roadmap: {
    displayName: <FormattedMessage defaultMessage="Road" />,
    description: <FormattedMessage defaultMessage="Streets & buildings" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-roadmap@2x.png',
    key: 'roadmap'
  },
  topo: {
    displayName: <FormattedMessage defaultMessage="U.S. Geological Survey (USGS)" />,
    description: <FormattedMessage defaultMessage="Historical topographic map of the U.S." />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-topo@2x.png',
    key: 'topo',
    requiresAdmin: false
  },
  worldparks: {
    displayName: <FormattedMessage defaultMessage="Terrain" />,
    description: <FormattedMessage defaultMessage="Topo map with park boundaries" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-layer-worldparks@2x.png',
    key: 'worldparks'
  },
  worldtopo: {
    displayName: <FormattedMessage defaultMessage="Terrain (legacy)" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-worldtopo@2x.png',
    key: 'worldtopo',
    requiresAdmin: true
  }
};

export const staticStyleCardConfigs: Record<StaticStyleId, StaticStyleCardConfig> = {
  adminOverlay: {
    displayName: 'Custom overlay',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-admin-overlay@2x.png',
    key: 'adminOverlay',
    requiresAdmin: true,
    requiresPLP: true,
    requiresAdminStyleSourceUrl: true
  },
  waymarkedCycling: {
    displayName: <FormattedMessage defaultMessage="Waymarked cycling" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-cycling@2x.png',
    key: 'waymarkedCycling',
    requires2d: true,
    requiresGPSies: false
  },
  waymarkedHiking: {
    displayName: <FormattedMessage defaultMessage="Waymarked hiking" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-hiking@2x.png',
    key: 'waymarkedHiking',
    requires2d: true,
    requiresGPSies: false
  },
  waymarkedMTB: {
    displayName: <FormattedMessage defaultMessage="Waymarked mountain biking" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-waymarked-mtb@2x.png',
    key: 'waymarkedMTB',
    requires2d: true,
    requiresGPSies: false
  }
};

export const dynamicStyleCardConfigs: Record<DynamicStyleId, DynamicStyleCardConfig> = {
  after: {
    displayName: 'Last 60 days',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-after@2x.png',
    key: 'after',
    // PLAT-2212 trails:manage
    requiresAdmin: true,
    requiresPLP: true
  },
  clusterTrails: {
    displayName: 'Cluster trails',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-cluster-trails@2x.png',
    key: 'clusterTrails',
    requires2d: true,
    requiresAdmin: true
  },
  contribs: {
    displayName: 'Contrib VRs',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-contribs@2x.png',
    key: 'contribs',
    requires2d: true,
    requiresAdmin: true
  },
  distanceMarkers: {
    displayName: <FormattedMessage defaultMessage="Distance markers" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-distance-markers@2x.png',
    key: 'distanceMarkers'
  },
  geoBoundaries: {
    displayName: 'Polygons',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-geo-boundaries@2x.png',
    key: 'geoBoundaries',
    requiresAdmin: true
  },
  groundConditions: {
    displayName: <FormattedMessage defaultMessage="Ground conditions" />,
    description: <FormattedMessage defaultMessage="Terrain & surface type" />,
    featureEntitlement: 'advanced_conditions',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-ground-conditions.png',
    key: 'groundConditions'
  },
  heatmap: {
    displayName: <FormattedMessage defaultMessage="Heatmap" />,
    description: <FormattedMessage defaultMessage="Recent trail activity" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-heatmap@2x.png',
    hideFromPLP: true,
    key: 'heatmap',
    requiresAdmin: true
  },
  limit: {
    displayName: 'Most recent 50',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-limit@2x.png',
    key: 'limit',
    requires2d: true,
    // PLAT-2212 trails:manage
    requiresAdmin: true
  },
  mapsAll: {
    displayName: 'Maps all',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-maps-all@2x.png',
    key: 'mapsAll',
    requires2d: true,
    requiresAdmin: true
  },
  nearbyTrails: {
    displayName: <FormattedMessage defaultMessage="Nearby trails" />,
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-nearby-trails@2x.png',
    key: 'nearbyTrails'
  },
  newHeatmap: {
    displayName: <FormattedMessage defaultMessage="Community heatmap" />,
    description: <FormattedMessage defaultMessage="Recent trail activity" />,
    featureEntitlement: 'community_heat_maps',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-communityHeatmap@2x.png',
    hideFromPLP: false,
    key: 'newHeatmap'
  },
  pendingTrails: {
    displayName: 'Pending trails',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-pending-trails@2x.png',
    key: 'pendingTrails',
    requiresAdmin: true
  },
  photos: {
    displayName: <FormattedMessage defaultMessage="Photos along the trail" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-photos@2x.png',
    key: 'photos'
  },
  photospot: {
    displayName: <FormattedMessage defaultMessage="Photospots" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-photospot@2x.png',
    key: 'photospot',
    requires2d: true,
    requiresAdmin: true
  },
  precipitation: {
    displayName: <FormattedMessage defaultMessage="Precipitation" />,
    description: <FormattedMessage defaultMessage="Real-time intensity & forecast" />,
    featureEntitlement: 'advanced_conditions',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-precipitation.png',
    key: 'precipitation'
  },
  recordingsAll: {
    displayName: 'Recordings all',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-recordings-all@2x.png',
    key: 'recordingsAll',
    requiresAdmin: true,
    requiresPLP: true
  },
  recordingsPop: {
    displayName: 'Recordings popular',
    hasHeatmap: true,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-recordings-pop@2x.png',
    key: 'recordingsPop',
    requiresAdmin: true
  },
  snowDepth: {
    displayName: <FormattedMessage defaultMessage="Snow depth" />,
    description: <FormattedMessage defaultMessage="Accumulated snowfall" />,
    featureEntitlement: 'advanced_conditions',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-snow-depth.png',
    key: 'snowDepth'
  },
  surfaceType: {
    displayName: <FormattedMessage defaultMessage="Surface type" />,
    key: 'surfaceType'
  },
  temperature: {
    displayName: <FormattedMessage defaultMessage="Temperature" />,
    description: <FormattedMessage defaultMessage="Fluctuations along the trail" />,
    featureEntitlement: 'advanced_conditions',
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-temperature.png',
    key: 'temperature'
  },
  waypoints: {
    displayName: <FormattedMessage defaultMessage="Waypoints" />,
    imgSrc: 'https://cdn-assets.alltrails.com/assets/map-layers/v3/map-overlay-waypoints@2x.png',
    key: 'waypoints'
  }
};
